import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Download, Loader2 } from 'lucide-react'
import type {SuperAgentGate, SuperAgentTarget} from '@/lib/api/features/super-agent';
import type {DesktopOs} from '@/lib/constants/desktopApp';
import {
  
  
  getSuperAgentStatus,
  offboardSuperAgent,
  onboardSuperAgent
} from '@/lib/api/features/super-agent'
import { useAiAvailable } from '@/lib/ai/useAiAvailable'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { superAgentAvatar } from '@/lib/utils/memberAvatar'
import {
  DESKTOP_OS_OPTIONS,
  
  SUPER_AGENT_DESKTOP,
  fetchLatestMacDownload
} from '@/lib/constants/desktopApp'

// Where this project's Super Agent work lands (project-level capability).
const TARGETS: Array<{ value: SuperAgentTarget; label: string; hint: string }> = [
  { value: 'code_pr', label: 'Code + PR', hint: 'Agent edits the code repo and opens a pull request → merge. Safest for code.' },
  { value: 'content_no_pr', label: 'Docs / content (no PR)', hint: 'Guarded: agent updates docs/content via tools — no PR, no merge, additive by default.' },
]

// Default per-task gate for this project (each task can override it in task details).
const GATES: Array<{ value: SuperAgentGate; label: string; hint: string }> = [
  { value: 'plan_first', label: 'Plan first', hint: 'Review the plan before the agent does the work.' },
  { value: 'direct', label: 'Direct', hint: 'Agent proceeds straight away (no plan review).' },
]

type Props = {
  workspaceSlug: string
  projectId: string
}

/**
 * Per-project Super Agent activation. Gated on the workspace being AI-enabled
 * (useAiAvailable). Enabling onboards the project (creates the AI-lane statuses, bot
 * user, plan-root page, config); disabling offboards it — the backend blocks disabling
 * while tasks are still in AI statuses, surfaced here as a clear error.
 */
export function SuperAgentSection({ workspaceSlug, projectId }: Props) {
  const aiAvailable = useAiAvailable()
  const canManage = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_AI_SETTINGS)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [target, setTarget] = useState<SuperAgentTarget>('code_pr')
  const [defaultGate, setDefaultGate] = useState<SuperAgentGate>('plan_first')
  const [activeAiTaskCount, setActiveAiTaskCount] = useState(0)
  const [downloadOs, setDownloadOs] = useState<DesktopOs>('mac-arm64')
  const [resolvingDownload, setResolvingDownload] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const status = await getSuperAgentStatus(workspaceSlug, projectId)
        if (cancelled) return
        setEnabled(status.enabled)
        setTarget(status.target ?? 'code_pr')
        setDefaultGate(status.defaultGate ?? 'plan_first')
        setActiveAiTaskCount(status.activeAiTaskCount ?? 0)
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, { context: 'super_agent_status' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceSlug, projectId])

  const disabledControls = !aiAvailable || !canManage || busy

  // Persist a Target/gate edit on an already-enabled project. The onboard endpoint is
  // idempotent — re-calling it updates the config's Target/DefaultGate in place — so it
  // doubles as the "update settings" path once the project is enabled.
  const persistConfig = async (next: { target?: SuperAgentTarget; defaultGate?: SuperAgentGate }) => {
    setBusy(true)
    try {
      await onboardSuperAgent(workspaceSlug, projectId, {
        target: next.target ?? target,
        defaultGate: next.defaultGate ?? defaultGate,
      })
      toast.success('Super Agent settings updated.')
      return true
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update the Super Agent settings.')
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleTargetChange = async (value: SuperAgentTarget) => {
    const prev = target
    setTarget(value)
    if (!(await persistConfig({ target: value }))) setTarget(prev)
  }

  const handleGateChange = async (value: SuperAgentGate) => {
    const prev = defaultGate
    setDefaultGate(value)
    if (!(await persistConfig({ defaultGate: value }))) setDefaultGate(prev)
  }

  // Resolve the newest macOS build via the public GitHub API, then trigger the download.
  // Filenames are versioned, so there's no static "latest" URL; on any failure we open
  // the releases page so the user can still grab it manually.
  const handleDownload = async () => {
    setResolvingDownload(true)
    try {
      const { url } = await fetchLatestMacDownload()
      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, { context: 'super_agent_download' })
      window.open(SUPER_AGENT_DESKTOP.releasesPage, '_blank', 'noopener,noreferrer')
    } finally {
      setResolvingDownload(false)
    }
  }

  const handleToggle = async (next: boolean) => {
    if (next) {
      setBusy(true)
      try {
        // Repository folder + agent harness are chosen per-machine in the desktop app
        // (Super Agent → Set folder), not here — enabling just creates the AI lane and
        // sets the working mode for the project.
        const status = await onboardSuperAgent(workspaceSlug, projectId, { target, defaultGate })
        setEnabled(true)
        setActiveAiTaskCount(status.activeAiTaskCount ?? 0)
        toast.success('Super Agent enabled for this project.')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not enable the Super Agent.')
      } finally {
        setBusy(false)
      }
    } else {
      setBusy(true)
      try {
        await offboardSuperAgent(workspaceSlug, projectId)
        setEnabled(false)
        toast.success('Super Agent disabled for this project.')
      } catch (error) {
        // 409 (tasks still in AI statuses) or other — keep it enabled and explain.
        toast.error(error instanceof Error ? error.message : 'Could not disable the Super Agent.')
      } finally {
        setBusy(false)
      }
    }
  }

  return (
    <>
      <Separator className="my-8" />
      <div>
        <div className="flex items-center gap-3 mb-1">
          <img
            src={superAgentAvatar}
            alt="Moly Super Agent"
            className="size-9 rounded-full shrink-0"
          />
          <p className="text-sm font-medium">Super Agent</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Let an AI coding agent pick up tasks assigned to the Super Agent in this project. Enabled projects
          appear in the desktop orchestrator, where you choose the local repository folder and agent for this
          machine.
        </p>

        {!aiAvailable && (
          <Alert className="mb-4">
            <AlertDescription>
              AI is not enabled for this workspace. Enable it in workspace AI settings before activating the
              Super Agent.
            </AlertDescription>
          </Alert>
        )}

        <div className="rounded-lg border bg-card p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="super-agent-toggle">Enable Super Agent</Label>
                  <p className="text-sm text-muted-foreground">
                    {enabled ? 'Active — this project is in the orchestrator.' : 'Off'}
                  </p>
                </div>
                <Switch
                  id="super-agent-toggle"
                  checked={enabled}
                  disabled={disabledControls}
                  onCheckedChange={(next) => void handleToggle(next)}
                />
              </div>

              {aiAvailable && enabled && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="super-agent-target">Target</Label>
                    <Select
                      value={target}
                      onValueChange={(v) => void handleTargetChange(v as SuperAgentTarget)}
                      disabled={disabledControls}
                    >
                      <SelectTrigger id="super-agent-target" className="w-full max-w-md">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGETS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {TARGETS.find((t) => t.value === target)?.hint}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="super-agent-gate">Default gate</Label>
                    <Select
                      value={defaultGate}
                      onValueChange={(v) => void handleGateChange(v as SuperAgentGate)}
                      disabled={disabledControls}
                    >
                      <SelectTrigger id="super-agent-gate" className="w-full max-w-md">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GATES.map((g) => (
                          <SelectItem key={g.value} value={g.value}>
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {GATES.find((g) => g.value === defaultGate)?.hint} Each task can override this in its details.
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="super-agent-os">Desktop app</Label>
                      <p className="text-sm text-muted-foreground">
                        The Super Agent runs on your machine. Download the desktop app, then paste your
                        workspace bot token to connect it.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Select value={downloadOs} onValueChange={(v) => setDownloadOs(v as DesktopOs)}>
                        <SelectTrigger id="super-agent-os" className="w-full sm:max-w-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DESKTOP_OS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} disabled={!o.available}>
                              {o.available ? o.label : `${o.label} — Coming soon`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={() => void handleDownload()}
                        disabled={resolvingDownload}
                        className="shrink-0"
                      >
                        {resolvingDownload ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Download className="size-4" />
                        )}
                        Download for Mac
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      For Macs with Apple Silicon (M1 or later).
                    </p>
                  </div>

                  {enabled && activeAiTaskCount > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                      {activeAiTaskCount} task(s) are still in AI statuses — move them to a human status before
                      disabling.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
